import React, { memo } from 'react'
import { HighlightedText } from './HighlightedText'
import { PathItem } from './PathItem'
import { Link } from '@tanstack/react-router'
import { useGetTaskTree } from '@/shared/hooks/search/useGetTaskTree'
import { Skeleton } from '@workspace/ui/components/Skeleton'
import { useInView } from 'react-intersection-observer'
import { LayoutDashboardIcon } from 'lucide-react'

interface TaskItemProps {
    projectId?: string | null
    title: string
    code?: string | null
    description?: string | null
    path?: string | null
    highlights?: {
        title?: string[]
        description?: string[]
        content?: string[]
    }
    onClick?: () => void
    id: string
}

export const TaskItem = memo(({ title, path, highlights, onClick, id, code, projectId }: TaskItemProps) => {
    const { ref, inView } = useInView({
        threshold: 0,
        triggerOnce: true,
    })
    const { data, isLoading } = useGetTaskTree(id, inView)

    const lastParentFolder = !!data?.parentFolders?.length && {
        name: data?.parentFolders?.at(-1)?.name || '',
        level: data?.parentFolders?.length || 0,
    }

    return (
        <Link
            ref={ref}
            to="/projects/$id/tasks"
            params={{
                id: projectId || '',
            }}
            search={{
                searchId: id,
                type: 'TASK',
            }}
            className="group flex items-start gap-3 py-3 hover:bg-neutral-50 dark:hover:bg-neutral-800 rounded-md px-2"
            onClick={onClick}
        >
            <LayoutDashboardIcon size={20} stroke="#737373" strokeWidth={1.5} />

            <div className="flex flex-col min-w-0 flex-1 gap-2">
                <div className="flex items-center justify-between gap-2">
                    <HighlightedText
                        text={title}
                        highlight={highlights?.title}
                        className="text-sm font-medium text-foreground truncate leading-5 group-hover:underline"
                    />

                    <HighlightedText text={code} className="text-sm font-medium text-foreground truncate leading-5" />
                </div>

                {isLoading ? (
                    <Skeleton className="h-4 w-48" />
                ) : (
                    <PathItem
                        parentFolder={
                            lastParentFolder
                                ? { name: lastParentFolder?.name || '', level: lastParentFolder?.level || 0 }
                                : undefined
                        }
                        projectName={data?.project?.name || ''}
                    />
                )}
            </div>
        </Link>
    )
})

TaskItem.displayName = 'TaskItem'
